import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, PhoneCall, Compass } from "lucide-react";

const LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/devis", label: "Devis gratuit" },
  { to: "/assurance-auto/", label: "Assurance Auto", ext: true },
  { to: "/assurance-habitation/", label: "Assurance Habitation", ext: true },
  { to: "/mutuelle-sante/", label: "Mutuelle Santé", ext: true },
  { to: "/prevoyance/", label: "Prévoyance", ext: true },
  { to: "/assurance-vie/", label: "Assurance Vie", ext: true },
  { to: "/assurance-professionnelle/", label: "Professionnels", ext: true },
  { to: "/courtier-assurance-menton/", label: "Courtier à Menton", ext: true },
  { to: "/courtier-assurance-nice/", label: "Courtier à Nice", ext: true },
  { to: "/courtier-assurance-monaco/", label: "Courtier à Monaco", ext: true },
];

export default function NotFound() {
  return (
    <div className="min-h-screen bg-obsidian text-ivory flex flex-col" data-testid="not-found-page">
      <div className="grain-overlay" aria-hidden="true" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 60% 45% at 50% 0%, rgba(212,175,55,0.08), transparent 60%)" }}
      />
      <header className="relative border-b hairline">
        <div className="mx-auto max-w-5xl px-4 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" data-testid="notfound-brand" className="font-serif text-2xl font-semibold tracking-tight">
            ASSU<span className="text-amber inline-block" style={{ transform: "translateY(-0.1em)" }}>–</span>FRANCE
          </Link>
          <Link
            to="/devis"
            data-testid="notfound-callback-cta"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ivory/50 hover:text-amber transition-colors"
          >
            <PhoneCall className="h-4 w-4" /> Être rappelé par un conseiller
          </Link>
        </div>
      </header>

      <main className="relative flex-1 mx-auto w-full max-w-5xl px-4 sm:px-8 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber/90 mb-6 flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-amber/60" /> Erreur 404
          </p>
          <h1 className="font-serif text-4xl sm:text-6xl leading-tight tracking-tight text-balance">
            Cette page s'est égarée.{" "}
            <span className="italic text-amber">Pas vous.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-ivory/55">
            L'adresse que vous cherchez n'existe pas ou a été déplacée.
            Laissez-nous vous remettre sur la bonne route&nbsp;:
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-5">
            <Link
              to="/devis"
              data-testid="notfound-quote-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-amber px-8 py-4 text-base font-semibold text-obsidian transition-all duration-300 hover:bg-amber-warm hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] hover:-translate-y-0.5"
            >
              Obtenir mon devis gratuit
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/"
              data-testid="notfound-home-link"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ivory/70 hover:text-amber border-b border-ivory/20 hover:border-amber pb-1 transition-colors duration-300"
            >
              <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
            </Link>
          </div>

          <div className="mt-16 border-t hairline pt-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/40 mb-6 flex items-center gap-2">
              <Compass className="h-4 w-4 text-amber/70" /> Pages les plus consultées
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="notfound-links">
              {LINKS.map((l) =>
                l.ext ? (
                  <a
                    key={l.label}
                    href={l.to}
                    data-testid={`notfound-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="rounded-xl border border-ivory/10 bg-white/[0.03] px-5 py-4 text-sm text-ivory/60 transition-all duration-300 hover:border-amber/50 hover:text-amber hover:-translate-y-0.5"
                  >
                    {l.label}
                  </a>
                ) : (
                  <Link
                    key={l.label}
                    to={l.to}
                    data-testid={`notfound-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="rounded-xl border border-ivory/10 bg-white/[0.03] px-5 py-4 text-sm text-ivory/60 transition-all duration-300 hover:border-amber/50 hover:text-amber hover:-translate-y-0.5"
                  >
                    {l.label}
                  </Link>
                )
              )}
            </div>
          </div>
        </motion.div>
      </main>

      <footer className="relative border-t hairline">
        <div className="mx-auto max-w-5xl px-4 sm:px-8 py-6 text-xs text-ivory/35 font-mono">
          © 2026 ASSU-FRANCE — Courtier en assurance · ORIAS n° 25003318
        </div>
      </footer>
    </div>
  );
}
